import { createApi } from "@reduxjs/toolkit/query/react";
import type { Draft } from "immer";
import { ApiEndpoints, baseQueryWithAuth } from "../../core";
import { toast } from "../../core/toast";
import {
  ApiResponse,
  EditOrganisationRequest,
  Organisation,
} from "../../types/organisation";

export const organisationApi = createApi({
  reducerPath: "organisationApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Organisation", "Organisations"],

  endpoints: (builder) => ({
    // Get all organisations
    getOrganisations: builder.query<Organisation[], void>({
      query: () => ({
        url: `${ApiEndpoints.getAllOrganisations}`,
        method: "GET",
      }),
      transformResponse: (res: any) => res?.results ?? res,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ orgid }) => ({
                type: "Organisation" as const,
                id: orgid,
              })),
              { type: "Organisation", id: "LIST" },
            ]
          : [{ type: "Organisation", id: "LIST" }],
    }),

    // Get organisation by ID
    getOrganisationById: builder.query<Organisation, string | { orgid: string; license?: number }>({
      query: (arg) => {
        const orgid = typeof arg === "string" ? arg : arg.orgid;
        const url = `${ApiEndpoints.organisation}/${orgid}`;
        return {
          url,
          method: "GET",
        };
      },
      transformResponse: (res: any): Organisation => res?.result ?? res?.results?.[0] ?? res,
      providesTags: (_result, _error, arg) => {
        const orgid = typeof arg === "string" ? arg : arg.orgid;
        return [{ type: "Organisation", id: orgid }];
      },
    }),

    // Create organisation
    createOrganisation: builder.mutation<
      ApiResponse<Organisation>,
      EditOrganisationRequest
    >({
      query: (body) => ({
        url: `${ApiEndpoints.createOrganisation}`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Organisation", id: "LIST" }],
    }),

    // Edit organisation
    editOrganisation: builder.mutation<
      ApiResponse<Organisation>,
      { orgid: string; data: EditOrganisationRequest }
    >({
      query: ({ orgid, data }) => ({
        url: `${ApiEndpoints.editOrganisation}/${orgid}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { orgid }) => [
        { type: "Organisation", id: orgid },
        { type: "Organisation", id: "LIST" },
      ],
    }),

    // Delete organisation with optimistic update
    deleteOrganisation: builder.mutation<ApiResponse<{ ok: boolean }>, string>({
      query: (orgid) => ({
        url: `${ApiEndpoints.deleteOrganisation}/${orgid}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Organisation", id: "LIST" }],
      async onQueryStarted(orgid, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          organisationApi.util.updateQueryData(
            "getOrganisations",
            undefined,
            (draft: Draft<Organisation[]>) => {
              return draft.filter((org) => org.orgid !== orgid);
            }
          )
        );

        try {
          await queryFulfilled;
        } catch (err: any) {
          patchResult.undo();
        }
      },
    }),

    // Toggle organisation status
    toggleOrganisationStatus: builder.mutation<
      ApiResponse<{ ok: boolean }>,
      { orgid: string; status: "Active" | "Inactive" }
    >({
      query: ({ orgid, status }) => ({
        url: `${ApiEndpoints.organisationStatusChange}/${orgid}`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (_result, _error, { orgid }) => [
        { type: "Organisation", id: orgid },
        { type: "Organisation", id: "LIST" },
      ],
      async onQueryStarted({ orgid, status }, { dispatch, queryFulfilled }) {
        // Optimistic update in list
        const patchList = dispatch(
          organisationApi.util.updateQueryData(
            "getOrganisations",
            undefined,
            (draft: Draft<Organisation[]>) => {
              const org = draft.find((o) => o.orgid === orgid);
              if (org) org.status = status;
            }
          )
        );

        // Optimistic update in detail (if cached)
        const patchDetail = dispatch(
          organisationApi.util.updateQueryData(
            "getOrganisationById",
            orgid,
            (draft: Draft<Organisation>) => {
              if (draft) draft.status = status;
            }
          )
        );

        try {
          await queryFulfilled;
        } catch (err: any) {
          patchList.undo();
          patchDetail.undo();
        }
      },
    }),
  }),
});

export const {
  useGetOrganisationsQuery,
  useGetOrganisationByIdQuery,
  useCreateOrganisationMutation,
  useEditOrganisationMutation,
  useDeleteOrganisationMutation,
  useToggleOrganisationStatusMutation,
} = organisationApi;
